<script lang="ts" setup>
import _ from 'lodash'
import axios from 'axios'
import { useI18n } from 'vue-i18n'
import { AppService } from '@/enums/AppService'
import { useAuthStore } from '@/stores/auth'
import { useAppStore } from '@/stores/app'
import { useAcademicYearHelper } from '@/composable/queries/helpers/useAcademicYearHelper'
import { useGroupQuery } from '@/composable/queries/Dara/groups'
import { useProgrammeHelper } from '@/composable/queries/helpers/useProgrammeHelper'
import { useProgrammeReportsQuery } from '@/composable/queries/Dara/programmes'
import { useCombinePdfsForGroups } from '@/composable/queries/Dara/organisationResources'
import { useGenerateOrganisationSummaryReport } from '@/composable/queries/Dara/organisation'
import { useGenerateMyUCPersonalStatement } from '@/composable/queries/Dara/organisationReports'
import Organisation from '@/api/Dara/Organisation'

const props = defineProps<{
  onDashboard: boolean
}>()

interface Form {
  programme_id: null | number,
  type: null | string,
  file_type: null | App.Enums.MyPDFFileType,
  organisation_academic_year_id: null | number | 'all',
  year_group_id: null | number,
  sub_group_id: null | number,
}

interface ErrorResponse {
  errors: {
    [key: string]: string[]
  }
  message: string
}

const authStore = useAuthStore()
const appStore = useAppStore()

const { t } = useI18n()

enum MyPDFFileType {
  pdf = 'pdf',
  xls = 'xls',
}

const { academicYears } = useAcademicYearHelper()
const { organisationProgrammesWithQuestionnaire } = useProgrammeHelper()

const selectedOrganisationId = computed(() => authStore.selected_organisation_id ?? 0)

const isExtraReportSelected = ref(false)

const selectedAppService = ref<App.Enums.ProgrammeAppService|null>(null)

const form = ref<Form>({
  programme_id: null,
  type: null,
  file_type: null,
  organisation_academic_year_id: null,
  year_group_id: null,
  sub_group_id: null,
})

const { mutate: combinePdfsForGroupsMutation, isPending: isCombinePdfsForGroupsPending } = useCombinePdfsForGroups(
  selectedOrganisationId,
  computed(() => form.value.programme_id ?? 0),
)

const { data: groupsData } = useGroupQuery(
  computed(() => typeof form.value.organisation_academic_year_id === 'number' ? form.value.organisation_academic_year_id : 0),
  computed(() => typeof form.value.organisation_academic_year_id === 'number'),
)

const { data: programmeReportsData } = useProgrammeReportsQuery(
  computed(() => form.value.programme_id ?? 0),
  computed(() => !!form.value.programme_id),
)

const isExtraReportPending = ref(false)

const generateSummaryReportMutation = useGenerateOrganisationSummaryReport()

const generateMyUCPersonalStatementMutation = useGenerateMyUCPersonalStatement()

const onMyUCPersonalStatementCreate = (state: App.Enums.Reports.PersonalStatementReportState) => {
  isExtraReportPending.value = true

  generateMyUCPersonalStatementMutation.mutate({
    state,
    file_type: form.value.file_type!,
    organisation_academic_year_id: form.value.organisation_academic_year_id!,
    year_group_id: form.value.year_group_id!,
    sub_group_id: form.value.sub_group_id!,
  }, {
    onSuccess: () => {
      isExtraReportPending.value = false

      if (props.onDashboard) {
        appStore.setSnackbar({
          colour: 'success',
          text: t('pages.dashboard.report_builder_success_downloads_page'),
        })
      }

      onReset()
    },
    onError: error => {
      isExtraReportPending.value = false

      if(axios.isAxiosError(error)) {
        const responseData = error.response?.data as ErrorResponse

        appStore.setSnackbar({
          colour: 'warning',
          text: responseData.message,
        })
      }
    },
  })
}

const programmeExtraReports = {
  [AppService.MyUC]: {
    reports: [
      {
        title: t('extra_reports.myuc_personal_statement_all'),
        value: 'myuc_personal_statement_all',
        file_types: [MyPDFFileType.pdf],
        onCreate: () => {
          onMyUCPersonalStatementCreate('all')
        },
      },
      {
        title: t('extra_reports.myuc_personal_statement_completed'),
        value: 'myuc_personal_statement_completed',
        file_types: [MyPDFFileType.pdf],
        onCreate: () => {
          onMyUCPersonalStatementCreate('completed')
        },
      },
      {
        title: t('extra_reports.myuc_personal_statement_started'),
        value: 'myuc_personal_statement_started',
        file_types: [MyPDFFileType.pdf],
        onCreate: () => {
          onMyUCPersonalStatementCreate('started')
        },
      },
    ],
  },

  [AppService.My1C]: {
    reports: [
      {
        title: t('extra_reports.my1c_summary'),
        value: 'my1c_summary',
        file_types: [MyPDFFileType.pdf],
        onCreate: () => {
          isExtraReportPending.value = true

          generateSummaryReportMutation.mutate({
            organisation_academic_year_id: form.value.organisation_academic_year_id!,
            programme_id: form.value.programme_id!,
            year_group_id: form.value.year_group_id!,
            sub_group_id: form.value.sub_group_id!,
          }, {
            onSuccess: () => {
              isExtraReportPending.value = false

              if (props.onDashboard) {
                appStore.setSnackbar({
                  colour: 'success',
                  text: t('pages.dashboard.report_builder_success_downloads_page'),
                })
              }

              onReset()
            },
            onError: error => {
              isExtraReportPending.value = false

              if(axios.isAxiosError(error)) {
                const responseData = error.response?.data as ErrorResponse

                appStore.setSnackbar({
                  colour: 'warning',
                  text: responseData.message,
                })
              }
            },
          })
        },
      },
    ],
  },

  [AppService.MyCC]: {
    reports: [
      {
        title: t('extra_reports.mycc_subject_area'),
        value: 'mycc_subject_area',
        file_types: [MyPDFFileType.xls],
        onCreate: () => {
          isExtraReportPending.value = true

          const { organisation_academic_year_id, programme_id, year_group_id, sub_group_id } = form.value

          const params = {
            organisation_academic_year_id: organisation_academic_year_id!,
            programme_id: programme_id!,
            year_group_id: year_group_id!,
            sub_group_id: sub_group_id!,
          }

          const sanitizedParams = _.omitBy(params, _.isNull)

          Organisation.getStudentInterestsAreaExcelForYearGroups(selectedOrganisationId.value, sanitizedParams).then(data => {
            const blob = new Blob([data], { type: 'xlsx' })
            const link = document.createElement('a')

            link.href = window.URL.createObjectURL(blob)
            link.download = t('extra_reports.mycc_subject_area_filename')
            link.click()

            isExtraReportPending.value = false

            appStore.setSnackbar({
              colour: 'success',
              text: t('pages.dashboard.report_builder_success_direct_download'),
            })

            onReset()
          })
        },
      },
    ],
  },

  [AppService.MyA]: {
    reports: [
      {
        title: t('extra_reports.mya_results_report_pdf'),
        value: 'mya_results_report_pdf',
        file_types: [MyPDFFileType.pdf],
        onCreate: () => {
          isExtraReportPending.value = true

          const { organisation_academic_year_id, programme_id, year_group_id, sub_group_id } = form.value

          generateSummaryReportMutation.mutate({
            organisation_academic_year_id: organisation_academic_year_id!,
            programme_id: programme_id!,
            year_group_id: year_group_id!,
            sub_group_id: sub_group_id!,
          }, {
            onSuccess: () => {
              isExtraReportPending.value = false

              if (props.onDashboard) {
                appStore.setSnackbar({
                  colour: 'success',
                  text: t('pages.dashboard.report_builder_success_downloads_page'),
                })
              }

              onReset()
            },
            onError: error => {
              isExtraReportPending.value = false

              if(axios.isAxiosError(error)) {
                const responseData = error.response?.data as ErrorResponse

                appStore.setSnackbar({
                  colour: 'warning',
                  text: responseData.message,
                })
              }
            },
          })
        },
      },
      {
        title: t('extra_reports.mya_results_report_xls'),
        value: 'mya_results_report_xls',
        file_types: [MyPDFFileType.xls],
        onCreate: () => {
          isExtraReportPending.value = true

          const { organisation_academic_year_id, programme_id, year_group_id, sub_group_id } = form.value

          const params = {
            organisation_academic_year_id: organisation_academic_year_id!,
            programme_id: programme_id!,
            year_group_id: year_group_id!,
            sub_group_id: sub_group_id!,
          }

          // Only sanitize optional fields, keep required ones
          const sanitizedParams = {
            organisation_academic_year_id: params.organisation_academic_year_id,
            programme_id: params.programme_id,
            ..._.omitBy({ year_group_id: params.year_group_id, sub_group_id: params.sub_group_id }, _.isNull),
          }

          Organisation.generateSummaryResultsExport(selectedOrganisationId.value, sanitizedParams).then(data => {
            const blob = new Blob([data], { type: 'xlsx' })
            const link = document.createElement('a')

            link.href = window.URL.createObjectURL(blob)
            link.download = t('extra_reports.mya_results_report_filename')
            link.click()

            isExtraReportPending.value = false

            appStore.setSnackbar({
              colour: 'success',
              text: t('pages.dashboard.report_builder_success_direct_download'),
            })

            onReset()
          })
        },
      },
    ],
  },
}

const programmeOptions = computed(() => organisationProgrammesWithQuestionnaire.value?.map(programme => ({
  title: programme.name,
  value: programme.id,
})))

const academicYearOptions = computed(() => {
  if (!academicYears.value || !academicYears.value.length) return []

  return [{
    title: t('pages.dashboard.report_builder_all_label'),
    value: 'all',
  }, ...academicYears.value
    .map(({ id, label }) => ({
      title: label,
      value: id,
    }))]
})

const yearGroupOptions = computed(() => {
  if (!groupsData.value || !groupsData.value.data.length) return []

  return [{
    title: t('pages.dashboard.report_builder_all_label'),
    value: 'all',
  }, {
    title: t('pages.dashboard.report_builder_none_label'),
    value: 'none',
  }, ...groupsData.value.data
    .filter(group => group.parent_group_id === null)
    .map(({ id, name }) => ({
      title: name,
      value: id,
    }))]
})

const subGroupOptions = computed(() => {
  if (!groupsData.value || !groupsData.value.data.length || !form.value.year_group_id) return []

  const selectedGroup = groupsData.value.data.find(group => group.id === form.value.year_group_id)
  if (!selectedGroup) return []

  return [{
    title: t('pages.dashboard.report_builder_all_label'),
    value: 'all',
  }, {
    title: t('pages.dashboard.report_builder_none_label'),
    value: 'none',
  }, ...selectedGroup.sub_groups.map(subGroup => ({
    title: subGroup.name,
    value: subGroup.id,
  }))]
})

function isValidAppService(value: unknown): value is keyof typeof programmeExtraReports {
  return value === AppService.My1C || value === AppService.MyCC || value === AppService.MyA || value === AppService.MyUC
}

const reportTypeOptions = computed(() => {
  if (!programmeReportsData.value) return []

  const reportTypes = Object.entries(programmeReportsData.value).map(([key, data]) => ({
    value: key,
    title: data.name,
  }))

  const extraReports = isValidAppService(selectedAppService.value)
    ? programmeExtraReports[selectedAppService.value].reports
    : []

  return [...reportTypes, ...extraReports]
})

const reportFileTypeOptions = computed(() => {
  isExtraReportSelected.value = false

  if (!programmeReportsData.value) return []

  const reportType = Object.entries(programmeReportsData.value).find(([key]) => key === form.value.type)
  if (!reportType) {
    const extraReport = isValidAppService(selectedAppService.value)
      ? programmeExtraReports[selectedAppService.value].reports.find((report: { value: string }) => report.value === form.value.type)
      : undefined

    if (extraReport) {
      isExtraReportSelected.value = true
    }

    return extraReport?.file_types.map((fileType: MyPDFFileType) => ({
      value: fileType,
      title: t(`pages.dashboard.report_builder_file_type_${fileType}`),
    })) || []
  }

  return reportType[1].types.map((fileType: string) => ({
    value: fileType,
    title: t(`pages.dashboard.report_builder_file_type_${fileType}`),
  }))
})

const onProgrammeChange = () => {
  selectedAppService.value = organisationProgrammesWithQuestionnaire.value?.find(programme => programme.id === form.value.programme_id)?.app_service ?? null
  form.value.type = null
  form.value.file_type = null
}

const onAcademicYearChange = () => {
  form.value.year_group_id = null
  form.value.sub_group_id = null
}

const isFormValid = computed(() => {
  const { programme_id, type, organisation_academic_year_id, year_group_id, file_type } = form.value

  return programme_id && type && organisation_academic_year_id && (year_group_id || organisation_academic_year_id === 'all') && file_type
})

const onCreate = () => {
  if (isFormValid.value) {
    if (isExtraReportSelected.value) {
      return isValidAppService(selectedAppService.value)
        ? programmeExtraReports[selectedAppService.value].reports.find((report: { value: string }) => report.value === form.value.type)?.onCreate()
        : undefined
    }

    const { programme_id, type, file_type, organisation_academic_year_id, year_group_id, sub_group_id } = form.value

    combinePdfsForGroupsMutation({
      programme_id: programme_id!,
      type: type!,
      file_type: file_type!,
      organisation_academic_year_id: organisation_academic_year_id!,
      year_group_id: year_group_id!,
      sub_group_id: sub_group_id!,
    }, {
      onSuccess: () => {
        if (props.onDashboard) {
          appStore.setSnackbar({
            colour: 'success',
            text: t('pages.dashboard.report_builder_success_downloads_page'),
          })
        }

        onReset()
      },
    })
  }
}

const onReset = () => {
  form.value = {
    programme_id: null,
    type: null,
    file_type: null,
    organisation_academic_year_id: null,
    year_group_id: null,
    sub_group_id: null,
  }
}
</script>

<template>
  <VCard class="report-builder-card w-100 pa-4 elevation-5">
    <h4 class="mb-3">
      {{ $t('pages.dashboard.report_builder_header') }}
    </h4>
    <p class="text-body-2">
      {{ $t('pages.dashboard.report_builder_text') }}
    </p>
    <VForm>
      <VRow class="mt-3">
        <VCol
          :cols="onDashboard ? 6 : 12"
          :class="{ 'py-1': onDashboard }"
        >
          <VSelect
            v-model="form.programme_id"
            :items="programmeOptions"
            :placeholder="$t('pages.dashboard.report_builder_programme_label')"
            :hide-details="true"
            density="compact"
            required
            @update:model-value="onProgrammeChange"
          />
        </VCol>
        <VCol
          :cols="onDashboard ? 6 : 12"
          :class="{ 'py-1': onDashboard }"
        >
          <VSelect
            v-model="form.type"
            :items="reportTypeOptions"
            :placeholder="$t('pages.dashboard.report_builder_report_label')"
            :disabled="!form.programme_id"
            :hide-details="true"
            density="compact"
            required
            @update:model-value="form.file_type = null"
          />
        </VCol>
      </VRow>
      <VRow>
        <VCol
          :cols="onDashboard ? 6 : 12"
          :class="{ 'py-1': onDashboard }"
        >
          <VSelect
            v-model="form.file_type"
            :items="reportFileTypeOptions"
            :placeholder="$t('pages.dashboard.report_builder_file_type_label')"
            :disabled="!form.type || !form.programme_id"
            :hide-details="true"
            density="compact"
            required
          />
        </VCol>
        <VCol
          :cols="onDashboard ? 6 : 12"
          :class="{ 'py-1': onDashboard }"
        >
          <VSelect
            v-model="form.organisation_academic_year_id"
            :items="academicYearOptions"
            :placeholder="$t('pages.dashboard.report_builder_academic_year_label')"
            :disabled="!form.programme_id || !form.file_type"
            :hide-details="true"
            density="compact"
            required
            @update:model-value="onAcademicYearChange"
          />
        </VCol>
      </VRow>
      <VRow>
        <VCol
          :cols="onDashboard ? 6 : 12"
          :class="{ 'py-1': onDashboard }"
        >
          <VSelect
            v-model="form.year_group_id"
            :items="yearGroupOptions"
            :placeholder="$t('pages.dashboard.report_builder_year_group_label')"
            :disabled="!form.organisation_academic_year_id || form.organisation_academic_year_id === 'all' || !form.file_type"
            :hide-details="true"
            density="compact"
            required
            @update:model-value="form.sub_group_id = null"
          />
        </VCol>
        <VCol
          :cols="onDashboard ? 6 : 12"
          :class="{ 'py-1': onDashboard }"
        >
          <VSelect
            v-model="form.sub_group_id"
            :items="subGroupOptions"
            :placeholder="$t('pages.dashboard.report_builder_sub_group_label', {
              subgroupName: authStore.organisationYearSubGroupKnownAs,
            })"
            :disabled="!form.year_group_id"
            :hide-details="true"
            density="compact"
            required
          />
        </VCol>
      </VRow>
      <VRow>
        <VCol
          cols="12"
          class="d-flex justify-end"
        >
          <VBtn
            rounded="xl"
            variant="tonal"
            class="submit-grey-btn text-none"
            :disabled="!isFormValid"
            :loading="isCombinePdfsForGroupsPending || isExtraReportPending"
            @click="onCreate"
          >
            {{ $t('pages.dashboard.report_builder_create_btn') }}
          </VBtn>
        </VCol>
      </VRow>
    </VForm>
  </VCard>
</template>

<style lang="scss">
@use "@sass/variables" as variables;

.report-builder-card {
  .v-field {
    border: 1px solid variables.$branding-grey-color;
    border-radius: 5px;
    .v-field__input {
      input::placeholder {
        color: variables.$branding-grey-color;
        opacity: 1;
      }
    }
  }
  .v-field__overlay, .v-field__overlay:hover {
    background-color: transparent !important;
  }
  .v-field__outline {
    display: none;
  }
}
</style>
